import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

const STORAGE_KEY = 'motwscid';
const COMPLETED_POLLS_KEY = 'pcop';

@Injectable({
  providedIn: 'root',
})
export class UserIdService {
  private userId: string | null = null;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.initializeUserId();
  }

  private initializeUserId(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const storedId = localStorage.getItem(STORAGE_KEY);

      if (storedId && this.isValidUUID(storedId)) {
        this.userId = storedId;
      } else {
        this.userId = this.generateUUID();
        localStorage.setItem(STORAGE_KEY, this.userId);
      }
    } catch (error) {
      console.error('Error accessing localStorage:', error);
      this.userId = this.generateUUID();
    }
  }

  getUserId(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    if (!this.userId) {
      this.initializeUserId();
    }

    return this.userId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private isValidUUID(uuid: string): boolean {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(uuid);
  }

  getCompletedPolls(): number[] {
    if (!isPlatformBrowser(this.platformId)) {
      return [];
    }

    try {
      const stored = localStorage.getItem(COMPLETED_POLLS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          return parsed.filter((id) => typeof id === 'number');
        }
      }
      return [];
    } catch (error) {
      console.error('Error reading completed polls:', error);
      return [];
    }
  }

  isPollCompleted(pollId: number): boolean {
    return this.getCompletedPolls().includes(pollId);
  }

  markPollAsCompleted(pollId: number): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      const completedPolls = this.getCompletedPolls();
      if (!completedPolls.includes(pollId)) {
        completedPolls.push(pollId);
        localStorage.setItem(COMPLETED_POLLS_KEY, JSON.stringify(completedPolls));
      }
    } catch (error) {
      console.error('Error marking poll as completed:', error);
    }
  }
}
